import { useState, useEffect } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import SelectField from '../../../components/Forms/Input/SelectField';
import { ivaTypeOptions } from '../productData';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { useDebitNote } from '../../../hooks/purchases/useDebitNote';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { calculateTaxFromTotalAndQty } from '../../../utils/tax/creditTaxCalculator';

interface ProductFormValues {
  productId: string;
  productName: string;
  ivaType: string;
  calculatedTotal: string;
  calculatedQty: string;
  unitCost: string;
  quantity: string;
  ivaTotal: string;
  totalCost: string;
  withIva: string;
  ivaUnit: string;
}

interface ProductOption {
  id: number;
  name: string;
  unit?: string;
}

interface Props {
  onAdd: (product: any) => void;
  productOptions: ProductOption[];
}

const AddDebitNoteProductForm = ({ onAdd, productOptions }: Props) => {
  const {
    form: { id: debitNoteId },
  } = useSelector((state: RootState) => state.basePurchase);

  const { clearProductToEdit, handleCancelProduct } = useDebitNote();

  const [form, setForm] = useState<ProductFormValues>({
    productId: '',
    productName: '',
    ivaType: 'GRAVADO',
    calculatedTotal: '',
    calculatedQty: '',
    unitCost: '',
    quantity: '',
    ivaTotal: '',
    totalCost: '',
    withIva: '',
    ivaUnit: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (form.calculatedTotal && form.calculatedQty && !form.quantity) {
      const total = parseFloat(form.calculatedTotal);
      const qty = parseFloat(form.calculatedQty);

      if (total > 0 && qty > 0) {
        const { unitCost, unitCostWithIva, ivaTotal, totalCost, ivaUnit } =
          calculateTaxFromTotalAndQty(total, qty);

        setForm((prev) => ({
          ...prev,
          unitCost: unitCost.toString(),
          withIva: unitCostWithIva.toString(),
          ivaUnit: ivaUnit.toString(),
          ivaTotal: ivaTotal.toString(),
          totalCost: totalCost.toString(),
        }));
      }
    }
  }, [form.calculatedTotal, form.calculatedQty]);

  const handleSave = () => {
    const newErrors: typeof errors = {};

    if (!form.productId) newErrors.productId = 'Producto requerido';
    if (!form.unitCost) newErrors.unitCost = 'Costo unitario requerido';
    if (!form.quantity && !form.calculatedQty)
      newErrors.quantity = 'Cantidad requerida';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newProduct = {
      debitNoteId,
      type: 'NEW_PRODUCT',
      productId: Number(form.productId),
      quantity: Number(form.quantity || form.calculatedQty),
      unitCost: Number(form.unitCost),
      taxType: form.ivaType === 'GRAVADO' ? 'TAXED' : 'EXEMPT',
    };

    onAdd(newProduct);
  };

  useEffect(() => {
    return () => {
      clearProductToEdit(); // o clearServiceToEdit()
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SimpleSearchSelect
          label="Producto"
          name="productId"
          iconKey="category"
          options={productOptions}
          selectedValue={form.productId ? Number(form.productId) : null}
          onSelect={(option: any) =>
            setForm((prev) => ({
              ...prev,
              productId: option.id,
              productName: option.name,
            }))
          }
          error={errors.productId}
          required
        />

        <SelectField
          label="Tipo"
          name="ivaType"
          iconKey="list"
          value={form.ivaType}
          onChange={() => {}} // sin cambio permitido
          options={ivaTypeOptions}
          disabled
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <InputField
          label="Costo Total (Calculadora)"
          name="calculatedTotal"
          iconKey="dollar"
          type="number"
          value={form.calculatedTotal}
          onChange={handleChange}
        />
        <InputField
          label="Cantidad (Calculadora)"
          name="calculatedQty"
          iconKey="product"
          type="number"
          value={form.calculatedQty}
          onChange={handleChange}
        />
        <InputField
          label="Costo Unitario"
          name="unitCost"
          iconKey="dollar"
          type="number"
          value={form.unitCost}
          onChange={handleChange}
          error={errors.unitCost}
          required
        />
        <InputField
          label="Cantidad"
          name="quantity"
          iconKey="product"
          type="number"
          value={form.quantity}
          onChange={handleChange}
          error={errors.quantity}
        />
        <InputField
          label="IVA Total"
          name="ivaTotal"
          iconKey="dollar"
          type="number"
          value={form.ivaTotal}
          onChange={handleChange}
          error={errors.ivaTotal}
        />
        <InputField
          label="Costo Total"
          name="totalCost"
          iconKey="dollar"
          type="number"
          value={form.totalCost}
          onChange={handleChange}
          error={errors.totalCost}
        />
      </div>

      <div className="text-gray-600 space-y-1 text-sm dark:text-white">
        <p>CON IVA: $ {form.withIva || '0.00000000'}</p>
        <p>IVA UNITARIO: $ {form.ivaUnit || '0.00000000'}</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={handleCancelProduct}
          className="inline-flex items-center gap-2 rounded-md bg-slate-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddDebitNoteProductForm;
